import axios from 'axios';

import { post } from '../utils/api';
import { uploadQueue } from './upload-queue';
import * as types from './types';

// The one way to put a file on S3.
//
// Every upload path in the app should call this. Before it existed there were two
// different functions named `uploadMediumFile` -- a redux action in ./actions.js
// that tracked state and emitted telemetry, and a bare `axios.put` wrapper in
// ./api.js that did neither -- and thirteen of the twenty-three call sites imported
// the second one. Five of those thirteen discarded the returned promise entirely, so
// an upload could fail while the form reported success and nothing anywhere recorded
// it. That is why production carries stalled media with no upload events at all.
//
// Properties this guarantees, which no previous path had all of:
//
//   * it never rejects. Resolves { ok: true } or { ok: false, error }. Call sites
//     that ignore the result cannot produce an unhandled rejection, and call sites
//     that `await` without a try/catch cannot throw out of a submit handler. Both
//     shapes exist all over this app and neither is safe today.
//   * it retries transient failures with backoff, which nothing did before.
//   * it emits the upload lifecycle itself, rather than leaving telemetry to be a
//     side effect of redux state that only some paths write to.
//   * it aborts on a stalled connection rather than on total elapsed time.
//   * it never puts the error object into a tracked event. `context: { ...error }`
//     spreads an axios error's `config`, which carries the Authorization header, so
//     live session tokens are sitting in events.context in production today.

// Progress points at which a `*_part_uploaded` event is emitted. Kept at 30/60/90
// so the series stays comparable with the events already in the database.
const PART_MILESTONES = [30, 60, 90];

// Abort when no progress event arrives for this long, instead of capping total
// request time.
//
// The previous transport set `timeout: 5 * 60 * 1000`, and in a browser axios
// timeout is XMLHttpRequest.timeout -- total wall clock, not idle time. Since the
// client admits files up to 2GB, that made any upload needing more than five
// minutes fail deterministically on a perfectly healthy connection: 2GB in 300s
// needs ~57Mbps sustained upstream. A stall timeout accepts a slow-but-live
// connection and still gives up promptly on a dead one.
const STALL_TIMEOUT_MS = 90 * 1000;

const MAX_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 1000;

// Retry only what a retry can fix. A 403 means the signature or the expiry is
// wrong and every attempt will fail the same way; a 400 means the request itself
// is malformed. Retrying either just multiplies the failure.
const isRetryable = (error) => {
  if (error && error.__stalled) return true;
  if (error && error.code === 'ECONNABORTED') return true;

  const status = error && error.response && error.response.status;
  if (!status) return true; // no response at all: network, reset, DNS, offline
  return status >= 500 || status === 429;
};

// The redux `entities` slice is keyed by the jsonapi entity type -- plural and
// lowercase -- so meta.mediumType must be 'images', not 'Image'. The reducer does
// `state[action.meta.mediumType][action.meta.id]` with no guard, so getting this
// wrong throws a TypeError out of the reducer and takes the page with it.
//
// This is what the redux action used to build inline as
// `${medium.type}s`.toLowerCase(), and passing the raw type through instead broke
// the member avatar upload.
const entityType = (mediumType, file) => {
  const prefix = eventPrefix(mediumType, file);
  return prefix ? `${prefix}s` : undefined;
};

// 'Image' | 'images' | 'IMAGE' -> 'image'. Falls back to the file's MIME type,
// because several call sites hold only an upload url and a blob.
const eventPrefix = (mediumType, file) => {
  const fromType = String(mediumType || '').toLowerCase().replace(/s$/, '');
  if (['image', 'video', 'audio'].includes(fromType)) return fromType;

  const mime = (file && file.type) || '';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return null;
};

// Only ever named fields. See the note above about tokens.
const describeError = (error) => {
  if (!error) return {};
  return {
    message: String(error.message || '').slice(0, 300),
    code: error.code || (error.__stalled ? 'ESTALLED' : undefined),
    status: (error.response && error.response.status) || undefined,
  };
};

const trackUpload = (prefix, suffix, mediumId, context) => {
  if (!prefix || !mediumId) return;

  // Fire and forget on purpose: telemetry must never delay or fail an upload. The
  // catch matters because the endpoint 500s on an unknown topic name and without
  // it that becomes an unhandled rejection.
  Promise.resolve(
    post(`/v2/me/tracking_events?event_topic_name=${prefix}_${suffix}`, {
      data: {
        attributes: {
          'subject-id': mediumId,
          'subject-type': 'Medium',
          context,
        },
      },
    })
  ).catch(() => {});
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A single PUT attempt, with a stall watchdog.
const putOnce = (url, file, { onProgress }) => {
  const source = axios.CancelToken.source();
  let lastActivity = Date.now();

  const watchdog = setInterval(() => {
    if (Date.now() - lastActivity < STALL_TIMEOUT_MS) return;
    clearInterval(watchdog);
    source.cancel('stalled');
  }, 5000);

  return axios
    .put(url, file, {
      headers: file && file.type ? { 'Content-Type': file.type } : undefined,
      cancelToken: source.token,
      onUploadProgress: (progressEvent) => {
        lastActivity = Date.now();
        if (!onProgress) return;

        // The guard is the point. story-media's old onUploadProgress computed
        // `loaded * 100 / total` unguarded, so a progress event without a total
        // produced NaN, every `pct >= milestone` comparison was false, and the
        // upload silently reported no progress at all. The Watch Me Grow variant
        // guarded it; the batch path did not.
        if (!progressEvent || !progressEvent.total) return;
        onProgress(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
      },
    })
    .then(
      (response) => {
        clearInterval(watchdog);
        return response;
      },
      (error) => {
        clearInterval(watchdog);
        if (axios.isCancel(error)) {
          const stalled = new Error(`upload stalled for ${STALL_TIMEOUT_MS}ms with no progress`);
          stalled.__stalled = true;
          throw stalled;
        }
        throw error;
      }
    );
};

/**
 * Upload a file to its medium's presigned S3 url.
 *
 * Never rejects. Resolves { ok, attempts, error? }.
 *
 * @param medium      the created medium, carrying 'upload-url', id and type.
 *                    Optional if url + mediumId are given directly.
 * @param url         presigned url, when the caller has no medium object.
 * @param mediumId    medium id, for telemetry. Without it no events are emitted.
 * @param mediumType  'Image' | 'Video' | 'Audio'; inferred from the file if absent.
 * @param file        Blob or File to upload.
 * @param dispatch    optional redux dispatch. When given, the upload appears in
 *                    the upload-manager UI exactly as before.
 * @param onProgress  optional (percent) => void.
 */
export const uploadMedium = async ({
  medium,
  url,
  mediumId,
  mediumType,
  file,
  dispatch,
  onProgress,
} = {}) => {
  const targetUrl = url || (medium && medium['upload-url']);
  const id = mediumId || (medium && medium.id);
  const prefix = eventPrefix(mediumType || (medium && medium.type), file);

  if (!targetUrl || !file) {
    const error = new Error(!targetUrl ? 'no upload url for medium' : 'no file to upload');
    trackUpload(prefix, 'upload_failed', id, { ...describeError(error), attempts: 0 });
    return { ok: false, attempts: 0, error };
  }

  const meta = {
    id,
    mediumType: entityType(mediumType || (medium && medium.type), file),
    fileSize: file.size,
    fileName: file.name,
  };

  // Without an entity type the entities reducer cannot key its lookup, and
  // dispatching would throw from inside the reducer. Skip the redux lifecycle
  // rather than crash the page -- the upload itself and its telemetry are
  // unaffected.
  const canDispatch = Boolean(dispatch && meta.mediumType && id);
  if (dispatch && !canDispatch) {
    // eslint-disable-next-line no-console
    console.warn('uploadMedium: no entity type for this medium, skipping redux upload state', {
      mediumId: id,
      mediumType,
    });
  }

  const reportedMilestones = new Set();
  const handleProgress = (percent) => {
    if (canDispatch) dispatch({ type: types.UPDATE_UPLOAD_PROGRESS, mediumId: id, progress: percent });
    if (onProgress) onProgress(percent, medium);

    PART_MILESTONES.forEach((milestone) => {
      if (percent < milestone || reportedMilestones.has(milestone)) return;
      reportedMilestones.add(milestone);
      trackUpload(prefix, 'part_uploaded', id, { percentage: milestone, file_size: file.size });
    });
  };

  if (canDispatch) dispatch({ type: types.UPLOAD_MEDIUM_START, meta });

  let lastError;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    // Emitted here, per attempt, when the PUT is actually about to be issued.
    // The old path emitted `*_upload_started` from a useEffect watching redux
    // state, which fired the moment a file was ENQUEUED -- so a whole batch
    // reported "started" while only one file was moving, and the event proved
    // nothing about whether a request was ever made.
    trackUpload(prefix, 'upload_started', id, { attempt, file_size: file.size });

    try {
      const startedAt = Date.now();
      // eslint-disable-next-line no-await-in-loop
      await uploadQueue.add(() => putOnce(targetUrl, file, { onProgress: handleProgress }), 'high');

      if (canDispatch) dispatch({ type: types.UPLOAD_MEDIUM_SUCCESS, meta });
      trackUpload(prefix, 'upload_completed', id, {
        attempt,
        file_size: file.size,
        duration_ms: Date.now() - startedAt,
      });
      return { ok: true, attempts: attempt };
    } catch (error) {
      lastError = error;

      if (attempt < MAX_ATTEMPTS && isRetryable(error)) {
        trackUpload(prefix, 'upload_retry', id, { ...describeError(error), attempt });
        // eslint-disable-next-line no-await-in-loop
        await sleep(BASE_BACKOFF_MS * 2 ** (attempt - 1) + Math.floor(Math.random() * 250));
        // eslint-disable-next-line no-continue
        continue;
      }
      break;
    }
  }

  if (canDispatch) dispatch({ type: types.UPLOAD_MEDIUM_FAIL, meta });
  trackUpload(prefix, 'upload_failed', id, {
    ...describeError(lastError),
    attempts: MAX_ATTEMPTS,
    file_size: file.size,
  });

  return { ok: false, attempts: MAX_ATTEMPTS, error: lastError };
};

export default uploadMedium;
